import React, { useRef } from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';

interface SwipeableFlowShellProps {
  routes: { id: string; name: string }[];
  selectedId: string;
  onSelect: (id: string) => void;
  children: React.ReactNode;
}

/**
 * 工艺流程图外壳：
 * - 移动端顶部显示可横向滚动的路线切换条；
 * - 在流程图上快速左右滑动即可切换到上一条/下一条路线，无需滚动到列表点击。
 */
const SwipeableFlowShell: React.FC<SwipeableFlowShellProps> = ({ routes, selectedId, onSelect, children }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const touch = useRef<{ x: number; y: number; t: number; multi: boolean } | null>(null);

  const index = routes.findIndex((r) => r.id === selectedId);

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, t: Date.now(), multi: e.touches.length > 1 };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touch.current;
    touch.current = null;
    if (!start || start.multi || e.changedTouches.length !== 1) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const dt = Date.now() - start.t;
    // 仅识别快速横向轻扫，避免与流程图拖拽/缩放手势冲突
    if (dt > 500 || Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const next = dx < 0 ? index + 1 : index - 1;
    if (next < 0 || next >= routes.length) return;
    onSelect(routes[next].id);
  };

  return (
    <Box onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          gap: 0.5,
          overflowX: 'auto',
          mb: 1,
          pb: 0.5,
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {routes.map((r) => (
          <Chip
            key={r.id}
            label={r.name}
            size="small"
            clickable
            color={r.id === selectedId ? 'primary' : 'default'}
            variant={r.id === selectedId ? 'filled' : 'outlined'}
            onClick={() => onSelect(r.id)}
            sx={{ flexShrink: 0 }}
          />
        ))}
      </Box>
      {children}
      <Typography
        variant="caption"
        sx={{
          display: { xs: 'block', sm: 'none' },
          textAlign: 'center',
          mt: 0.5,
          color: isDarkMode ? '#999999' : 'text.secondary',
        }}
      >
        {index + 1} / {routes.length} · 左右滑动切换工艺流程
      </Typography>
    </Box>
  );
};

export default SwipeableFlowShell;
